import { GameSizeMetrics } from 'src/games/common/phaser/game/scenes/scaled-base-scene';
import { GameServices } from '../../services/game-services';
import { Shape } from 'src/games/phaserblocks/logic/models/shape';
import { BlockViewModel } from './block-view-model';
import { BehaviorSubject } from 'rxjs';

export type OnDragAction = (dragData: DragData) => void;

export enum DragActionType {
  OnStart,
  OnDrag,
  OnEnd,
}

export type DragData = {
  type: DragActionType;
  dragX: number;
  dragY: number;
  blockPositions: { x: number; y: number }[];
  shapeViewModel: ShapeViewModel;
};

export class ShapeViewModel {
  public get blocksLogicActionBus$() {
    return this.gameServices.gameRunnerService.blocksLogicActionBus$;
  }
  public get onGameSetupStartTrigger() {
    return this.gameServices.gameRunnerService.onGameSetupStartTrigger;
  }
  public get layoutConfig() {
    return this.gameServices.gameMetricsService.gameLayoutConfig;
  }
  public get currentShape() {
    return this.shape;
  }
  public get index() {
    return this._index;
  }
  public get allBlockViewModels() {
    return this.blockViewModels;
  }
  public get currentlyDragging() {
    return this.isDragging.getValue();
  }
  public get isDragging$() {
    return this.isDragging.asObservable();
  }
  public get currentlyValid() {
    return this.isValid.getValue();
  }
  public get isValid$() {
    return this.isValid.asObservable();
  }
  public get currentDepth$() {
    return this.depth.asObservable();
  }
  public set onDragAction(value: OnDragAction) {
    this.dragAction = value;
  }

  private readonly shape: Shape;
  private readonly _index: number;
  private readonly blockViewModels: BlockViewModel[];
  private readonly depth: BehaviorSubject<number>;
  private readonly isValid: BehaviorSubject<boolean>;
  private readonly isDragging: BehaviorSubject<boolean>;
  private dragAction?: OnDragAction;

  constructor(
    private gameServices: GameServices,
    shape: Shape,
    index: number,
  ) {
    this.shape = shape;
    this._index = index;
    this.blockViewModels = shape.blocks.map(
      (block) => new BlockViewModel(gameServices, block, shape, index),
    );
    this.depth = new BehaviorSubject<number>(0);
    this.isValid = new BehaviorSubject<boolean>(true);
    this.isDragging = new BehaviorSubject<boolean>(false);
  }

  public calculateScaleFactor(gameSizeMetrics: GameSizeMetrics): {
    scaledOffsetX: number;
    scaleFactor: number;
  } {
    return this.gameServices.gameMetricsService.calculateBoardScaling(gameSizeMetrics);
  }

  public calculateTargetPosition(): { x: number; y: number } {
    const layoutConfig = this.layoutConfig;

    const cellSz = layoutConfig.boardHeight / 9;
    const shapeWidth = layoutConfig.boardWidth / 3;
    const shapeHeight = cellSz * 3;

    const xPos = layoutConfig.hMargin + shapeWidth * 0.5 + shapeWidth * this.index;
    const yPos = layoutConfig.vMargin + shapeHeight * 0.5 + layoutConfig.boardHeight;
    return {
      x: xPos,
      y: yPos,
    };
  }

  public setValid(isValid: boolean) {
    this.isValid.next(isValid);

    this.blockViewModels.forEach((blockViewModel) => {
      blockViewModel.setValid(isValid);
    });
  }

  public highlightBlocks(ids: string[]) {
    this.blockViewModels.forEach((blockViewModel) => {
      blockViewModel.highlightBlock(ids.includes(blockViewModel.block.id));
    });
  }

  public dragStart(dragX: number, dragY: number, blockPositions: { x: number; y: number }[]) {
    this.dragAction?.({
      type: DragActionType.OnStart,
      dragX: dragX,
      dragY: dragY,
      blockPositions,
      shapeViewModel: this,
    });
    this.isDragging.next(true);
    this.depth.next(1);
  }

  public drag(dragX: number, dragY: number, blockPositions: { x: number; y: number }[]) {
    this.dragAction?.({
      type: DragActionType.OnDrag,
      dragX: dragX,
      dragY: dragY,
      blockPositions,
      shapeViewModel: this,
    });
  }

  public dragEnd(dragX: number, dragY: number, blockPositions: { x: number; y: number }[]) {
    this.dragAction?.({
      type: DragActionType.OnEnd,
      dragX: dragX,
      dragY: dragY,
      blockPositions,
      shapeViewModel: this,
    });
    this.isDragging.next(false);
    this.depth.next(0);
  }
}
